import type React from 'react';
import { Badge } from '@/components/UI';
import { trimDecimals } from '@einhaus/meteoric-helpers';
import type { PortfolioBacktestForwardEvaluation, PortfolioBacktestForwardSegmentStats } from '@einhaus/project314-types';
import { WALK_FORWARD_DISCLAIMER } from './portfolioBacktestColumnConfig';

const VERDICT_BADGE_VARIANTS = {
    passed: 'success',
    failed: 'danger',
    inconclusive: 'warning'
} as const;

const formatSegment = (segment: PortfolioBacktestForwardSegmentStats): string => {
    if (segment.tradeCount === 0) return 'no trades';

    const averageReturn = segment.avgProfitPercent !== null ? `${trimDecimals(segment.avgProfitPercent, 2)}%/trade` : 'no return data';
    const winRate = segment.winRatePercent !== null ? `, ${trimDecimals(segment.winRatePercent, 1)}% win rate` : '';

    return `${averageReturn} over ${segment.tradeCount.toLocaleString()} trades${winRate}`;
};

/**
 * Builds the hover text for a walk-forward cell. Candidate selection happens in training; this single
 * sealed out-of-sample segment supplies the verdict.
 */
const buildWalkForwardTooltip = (evaluation: PortfolioBacktestForwardEvaluation): string => {
    const captureRatio = evaluation.captureRatioPercent !== null ? `${trimDecimals(evaluation.captureRatioPercent, 1)}%` : 'not measurable';

    const lines = [
        `Verdict: ${evaluation.verdict.status.toUpperCase()}`,
        `Walk-forward window: ${evaluation.trainingEndDateTime} through ${evaluation.walkForwardEndDateTime}`,
        '',
        `Signal stream (decides the verdict): ${formatSegment(evaluation.stream.walkForward)}`,
        `Portfolio (shown in the column): ${formatSegment(evaluation.portfolio.walkForward)}`,
        `Capture ratio (portfolio vs stream, in-sample): ${captureRatio}`
    ];

    if (evaluation.verdict.reasons.length > 0) {
        lines.push('', ...evaluation.verdict.reasons.map((reason) => `• ${reason}`));
    }

    lines.push('', WALK_FORWARD_DISCLAIMER);

    return lines.join('\n');
};

interface PortfolioBacktestWalkForwardCellProps {
    forwardEvaluation: PortfolioBacktestForwardEvaluation | null | undefined;
}

const PortfolioBacktestWalkForwardCell: React.FC<PortfolioBacktestWalkForwardCellProps> = ({ forwardEvaluation }) => {
    if (!forwardEvaluation) {
        return (
            <span className="text-zinc-500" title={WALK_FORWARD_DISCLAIMER}>
                -
            </span>
        );
    }

    const walkForward = forwardEvaluation.portfolio.walkForward;
    if (!walkForward) {
        return (
            <span className="text-zinc-500" title={WALK_FORWARD_DISCLAIMER}>
                -
            </span>
        );
    }

    const { avgProfitPercent: walkForwardReturn, tradeCount } = walkForward;

    const returnColorClass =
        walkForwardReturn === null || walkForwardReturn === 0
            ? 'text-zinc-400'
            : walkForwardReturn > 0
              ? 'text-profit-green !text-[#29a740]'
              : 'text-red-500';

    const tooltip = buildWalkForwardTooltip(forwardEvaluation);

    return (
        <div className="flex flex-col items-start gap-1" title={tooltip}>
            <Badge variant={VERDICT_BADGE_VARIANTS[forwardEvaluation.verdict.status]}>{forwardEvaluation.verdict.status}</Badge>
            {tradeCount === 0 ? (
                <span className="text-xs text-zinc-500">no OOS trades</span>
            ) : (
                <>
                    <span className={`text-xs ${returnColorClass}`}>
                        {walkForwardReturn === null ? 'no return data' : `${trimDecimals(walkForwardReturn, 2)}%/trade`}
                    </span>
                    <span className="text-xs text-zinc-500">{tradeCount.toLocaleString()} OOS trades</span>
                </>
            )}
        </div>
    );
};

export default PortfolioBacktestWalkForwardCell;
